import React from 'react';

/**
 * StaticBodyBackdrops — fixed-position bodybuilder images that stay visible
 * across the entire landing page as the user scrolls. The page sections
 * scroll OVER the backdrop, so the bodybuilder photos are visible at the
 * top, middle and bottom of the page.
 *
 * Why `position: fixed` (not `absolute`):
 *   - `absolute` ties the image to its parent — they'd disappear once the
 *     hero section scrolled out of view.
 *   - `fixed` positions relative to the viewport, so the photos stay in
 *     place while content scrolls past them.
 *
 * Why pointer-events-none + z-0:
 *   - Sections above (z-10+) remain interactive.
 *   - The backdrop never blocks clicks/scrolls.
 *
 * Sections that need to fully cover the backdrop (e.g., FAQ, Pricing) get
 * a solid `bg-[#0a0e14]` class. Hero, blog cards and other "translucent"
 * sections let the bodybuilder show through at low opacity.
 */
/**
 * NOTE on the image URLs:
 * Both URLs MUST exactly match the `<link rel="preload" as="image">`
 * entries in `/app/frontend/public/index.html` (down to query-string
 * order) — otherwise the preload's resource won't be reused by the
 * CSS `background-image` and the browser will fetch the image twice,
 * undoing the LCP win.
 *
 * Mobile media query (max-width: 767px) gets the same URL via the
 * mobile div below, which loads in the LCP path on iPhone too.
 */
const BG_MALE =
  'https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?auto=format&fit=crop&w=900&q=65&fm=webp';
const BG_FEMALE =
  'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=900&q=65&fm=webp';

export function StaticBodyBackdrops() {
  // NOTE: Mobile hero backdrop is now rendered directly in `public/index.html`
  // (see `#fm-mobile-hero`) so it can paint during HTML parse — that eliminates
  // the ~2.3s "Element render delay" LCP flag PSI was raising on mobile Slow-4G.
  // React only renders the desktop side-column backdrops below.

  return (
    <>
      {/* Desktop / tablet: full-height fixed images on left and right edges.
          Pinned to the viewport so they're visible at every scroll position. */}
      <div
        aria-hidden="true"
        data-testid="landing-bg-female-fixed"
        className="hidden md:block fixed left-0 top-0 h-screen w-1/4 max-w-md pointer-events-none z-0"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(10,14,20,0) 0%, rgba(10,14,20,0.45) 60%, rgba(10,14,20,1) 100%), url(${BG_FEMALE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          opacity: 0.5,
        }}
      />
      <div
        aria-hidden="true"
        data-testid="landing-bg-male-fixed"
        className="hidden md:block fixed right-0 top-0 h-screen w-1/4 max-w-md pointer-events-none z-0"
        style={{
          backgroundImage: `linear-gradient(to left, rgba(10,14,20,0) 0%, rgba(10,14,20,0.45) 60%, rgba(10,14,20,1) 100%), url(${BG_MALE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          opacity: 0.5,
        }}
      />
    </>
  );
}

export default StaticBodyBackdrops;
